import type { PermissionDTO } from "../../domain/dto/RolesPermissionsDTO";
import type { Permission, Role } from "../../domain/entities/RolesPermissions";

export interface PermissionGroup {
  title: string;
  items: { id: string; label: string; description?: string }[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flattenRolePermissions = (data: any): any[] => {
  if (Array.isArray(data?.permissions) && data.permissions.length > 0) {
    return data.permissions;
  }

  return (data?.categories ?? []).flatMap(
    (category: { permissions?: any[] }) => category.permissions ?? [],
  );
};

export const mapPermissionDTO = (dto: PermissionDTO): Permission => ({
  id: dto.id,
  name: dto.generic_name || dto.name,
});

// Handle both old format (name) and new format (slug/generic_name) from role by ID API
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapRolePermissionDTO = (dto: any): Permission => ({
  id: dto.id,
  name: dto.generic_name || dto.slug || dto.name,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapRoleDTO = (dto: any): Role => ({
  id: dto?.id ?? "",
  name: dto?.name ?? "",
  description: dto?.description ?? "",
  permissions: flattenRolePermissions(dto).map(mapRolePermissionDTO),
  members_count: dto?.members_count ?? 0,
  access_level: dto?.access_level,
  total_permissions: dto?.total_permissions,
  granted_permissions: dto?.granted_permissions,
});

// Handle role by ID response which has permissions with slug, generic_name and scope
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapRoleByIdDTO = (data: any): Role => {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    permissions: flattenRolePermissions(data).map((perm: any) => ({
      id: perm.id,
      slug: perm.slug,
      generic_name: perm.generic_name,
      description: perm.description,
      scope: perm.scope,
      name: perm.generic_name || perm.slug || perm.name,
    })),
    members_count: data?.members_count ?? 0,
    access_level: data?.access_level,
    total_permissions: data?.total_permissions,
    granted_permissions: data?.granted_permissions,
  };
};

export const mapPermissionsDTO = (
  dtos: PermissionDTO[] | undefined,
): Permission[] => dtos?.map(mapPermissionDTO) ?? [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapRolesDTO = (dtos: any[] | undefined): Role[] =>
  dtos?.map(mapRoleDTO) ?? [];

export const mapPermissionsToGroups = (
  permissions: Permission[],
): PermissionGroup[] => {
  const getCategory = (name: string): string => {
    const lower = name.toLowerCase();
    if (lower.includes("patient")) return "Patients";
    if (lower.includes("clinical")) return "Clinical Context";
    if (lower.includes("screen")) return "Screening (Mayamind Test)";
    if (lower.includes("appointment")) return "Scheduling Appointments";
    if (lower.includes("user")) return "User Management";
    return "Other";
  };

  const grouped: Record<string, PermissionGroup> = {};

  permissions.forEach((p) => {
    const category = getCategory(p.name);
    if (!grouped[category]) {
      grouped[category] = { title: category, items: [] };
    }
    grouped[category].items.push({ id: p.id, label: p.name });
  });

  return Object.values(grouped).filter((g) => g.items.length > 0);
};
